const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/* =====================================================================
   BUNCH — FREE GIFT CART RECONCILER                     (added 2026-07-26)
                                                        (fixed 2026-07-28)
                                              (B2G2 carve-out 2026-07-30)
   =====================================================================
   Keeps the "Free Gift 🎁" line in sync with the REAL cart, no matter where
   the cart is changed from: Build Your Own Bunch, /cart, the cart drawer,
   product pages, reloads or the back button.

   THE MODEL: one gift product whose CONTENTS are a surprise and vary by
   order. Nothing is assigned to a tier — what steps up with cart size is the
   NUMBER of gifts. Tiers live in snippets/bunch-gift-tiers.liquid.

   WHY IT LIVES HERE: constants.js is loaded by layout/theme.liquid on every
   page, so this is the cheapest global hook. If you move it, make sure the
   replacement also loads site-wide.

   ---------------------------------------------------------------------
   WHAT WAS FIXED 2026-07-28

   1. THE DRAWER NEVER REPAINTED. refreshUI() called applySections(), which
      only looked for '#shopify-section-<id>'. The cart drawer is rendered
      from layout/theme.liquid via {% render 'cart-drawer' %} — a SNIPPET, not
      a section — so '#shopify-section-cart-drawer' does not exist. The
      function exited silently: the server cart was corrected but the shopper
      kept seeing the old number. That is why a gift could visibly sit at
      quantity 5. FIX: SECTION_TARGETS maps each section id to a real node.

   2. RACE WITH cart.js, which repaints the drawer from its own fetch
      immediately after a change. FIX: we repaint from a fresh server render
      after reconciling, and publish PUB_SUB_EVENTS.cartUpdate.

   3. 'internal' WAS A SHARED BOOLEAN, so overlapping writes could clear it
      early. FIX: depth counter.

   4. TIERS WERE HARDCODED IN THREE FILES WITH DIFFERENT VALUES.
      FIX: reads window.BUNCH_TIERS.

   5. lastEligible is published so the checkout handler
      (snippets/bunch-checkout-discount.liquid) can decide synchronously
      whether to route through /discount/... A checkout click must never wait
      on a network round trip.

   ---------------------------------------------------------------------
   WHAT CHANGED 2026-07-30 — THE B2G2 CARVE-OUT (three lines, read this)

   The Buy-2-Get-2-Free campaign page tags its cart lines:
       _b2g2       = 'true'   a paid jar bought through the campaign
       _b2g2_free  = 'true'   one of the 2 free picks

   Both are now SKIPPED ENTIRELY by this reconciler — they are neither gift
   lines nor eligible lines. Two separate bugs are being prevented:

   A. DOUBLE-DIPPING. Without the carve-out, adding 2 campaign jars would
      trip the "2 items -> 1 surprise gift" tier, so a B2G2 shopper would
      walk away with 2 free picks AND a surprise gift. Two reward programmes
      firing off the same two items.

   B. THE FREE PICKS BEING EATEN. isGiftLine() matches on
      properties._free_gift OR the gift variant id. If the campaign had
      reused _free_gift, this reconciler would have treated the 2 free picks
      as duplicate gift lines, zeroed them and collapsed them into one
      surprise gift — silently destroying the campaign cart on every page
      load. That is exactly why the campaign uses its own property names.
      DO NOT rename them to _free_gift.

   A knock-on effect, and it is the one we want: lastEligible no longer
   counts campaign lines, so a campaign-only cart never reaches the BUNCH10
   threshold and the checkout redirect never fires on it. BUNCH10 does not
   combine with product discounts, so applying it would strip the 100%-off
   from the free picks and charge for them. The shopper loses nothing — the
   store's automatic "10% off 5+ items" discount gives the same 10% with no
   code and IS set to combine.

   SCOPE: this is a UX guard, not a business control. It can be bypassed with
   JS disabled or a direct POST to /cart/change.js. The durable fix is to stop
   selling the gift as a Rs.0 product and give it away with a 100%-off Product
   Discount Function instead — which is precisely how B2G2 does it, and why
   B2G2 needs no reconciler of its own.
   ===================================================================== */
(function () {
  'use strict';

  if (window.BunchGiftSync) return; // never install twice

  /* ---------- CONFIG ---------- */
  var CFG = window.BUNCH_TIERS || {};
  var GIFT_VARIANT_ID = CFG.giftVariantId || 55021100269860;
  var TIERS = CFG.tiers || [                     // highest threshold first
    { min: 5, gifts: 3 },
    { min: 3, gifts: 2 },
    { min: 2, gifts: 1 }
  ];
  var SECTIONS = 'cart-items,cart-icon-bubble,cart-live-region-text,cart-footer,cart-drawer';
  var DEBUG = false;

  var SECTION_TARGETS = {
    'cart-drawer': { hosts: ['#CartDrawer .drawer__inner'], inner: '.drawer__inner' },
    'cart-icon-bubble': { hosts: ['#shopify-section-cart-icon-bubble', '#cart-icon-bubble'], inner: null }
  };

  /* ---------- STATE ---------- */
  var nativeFetch = window.fetch.bind(window);
  var running = false;    // a reconcile pass is in flight
  var queued = false;     // another pass was requested while running
  var internalDepth = 0;  // >0 while WE are writing to the cart

  function isInternal() { return internalDepth > 0; }

  function log() {
    if (DEBUG) console.log.apply(console, ['[gift-sync]'].concat([].slice.call(arguments)));
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function giftsFor(n) {
    for (var i = 0; i < TIERS.length; i++) { if (n >= TIERS[i].min) return TIERS[i].gifts; }
    return 0;
  }

  function isGiftLine(item) {
    return (
      item.variant_id === GIFT_VARIANT_ID ||
      (item.properties && item.properties._free_gift === 'true')
    );
  }

  /* Buy-2-Get-2-Free campaign lines. Invisible to this reconciler by design —
     see "THE B2G2 CARVE-OUT" above before touching this. */
  function isB2G2Line(item) {
    var p = item.properties;
    return !!p && (p._b2g2 === 'true' || p._b2g2_free === 'true');
  }

  /* ---------- CART WRITES (always by line item KEY, never variant id) ---------- */
  function internalPost(url, payload) {
    internalDepth++;
    return nativeFetch(root() + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); })
      .then(function (d) { internalDepth--; return d; })
      .catch(function (e) { internalDepth--; throw e; });
  }

  function changeLine(key, quantity) {
    return internalPost('cart/change.js', { id: key, quantity: quantity, sections: SECTIONS });
  }

  function addGift(quantity) {
    return internalPost('cart/add.js', {
      items: [{ id: GIFT_VARIANT_ID, quantity: quantity, properties: { _free_gift: 'true' } }],
      sections: SECTIONS
    });
  }

  /* ---------- RE-RENDER ---------- */
  function swap(sectionId, html) {
    var target = SECTION_TARGETS[sectionId];
    var doc = new DOMParser().parseFromString(html, 'text/html');

    if (!target) {
      var dest = document.getElementById('shopify-section-' + sectionId);
      var src = doc.getElementById('shopify-section-' + sectionId);
      if (!dest || !src) return false;
      dest.innerHTML = src.innerHTML;
      return true;
    }

    var node = null;
    for (var i = 0; i < target.hosts.length; i++) {
      node = document.querySelector(target.hosts[i]);
      if (node) break;
    }
    if (!node) return false;

    var source = target.inner ? doc.querySelector(target.inner) : doc.body.firstElementChild;
    if (!source) return false;

    node.innerHTML = source.innerHTML;
    return true;
  }

  function applySections(sections) {
    if (!sections) return false;
    var applied = false;
    Object.keys(sections).forEach(function (id) {
      if (swap(id, sections[id])) applied = true;
    });
    return applied;
  }

  function announce() {
    document.dispatchEvent(new CustomEvent('bunch:gift-synced'));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bunch-gift-sync' }); } catch (e) { /* noop */ }
    }
  }

  function refreshFromServer() {
    return nativeFetch(root() + '?sections=' + SECTIONS, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        applySections(sections);
        announce();
      })
      .catch(function (e) { log('refresh error', e); });
  }

  function refreshUI(lastResponse) {
    var ok = applySections(lastResponse && lastResponse.sections);
    if (ok) {
      announce();
      // cart.js may repaint with pre-correction HTML a beat after us.
      setTimeout(refreshFromServer, 150);
      return;
    }
    if (/^\/cart(\/|$)/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }
    refreshFromServer();
  }

  /* ---------- MAIN RECONCILE ---------- */
  function reconcile() {
    if (running) { queued = true; return Promise.resolve(); }
    running = true;

    return nativeFetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var eligible = 0;
        var giftLines = [];

        (cart.items || []).forEach(function (item) {
          if (isB2G2Line(item)) return;   // campaign lines: own reward, own rules
          if (isGiftLine(item)) giftLines.push(item);
          else eligible += item.quantity;
        });

        /* Published for snippets/bunch-checkout-discount.liquid, which must
           decide synchronously on a checkout click. */
        window.BunchGiftSync.lastEligible = eligible;

        var have = giftLines.reduce(function (s, l) { return s + l.quantity; }, 0);
        var need = giftsFor(eligible);
        log('eligible=' + eligible, 'have=' + have, 'need=' + need);

        if (have === need && giftLines.length <= 1) return null;

        var chain = Promise.resolve(null);

        if (giftLines.length === 0) {
          if (need > 0) chain = chain.then(function () { return addGift(need); });
        } else {
          // Collapse any duplicate gift lines into the first one.
          giftLines.slice(1).forEach(function (line) {
            chain = chain.then(function () { return changeLine(line.key, 0); });
          });
          chain = chain.then(function () { return changeLine(giftLines[0].key, need); });
        }
        return chain;
      })
      .then(function (last) {
        running = false;
        if (last) refreshUI(last);
        if (queued) { queued = false; return reconcile(); }
      })
      .catch(function (e) {
        running = false;
        log('error', e);
      });
  }

  window.BunchGiftSync = {
    reconcile: reconcile,
    giftVariantId: GIFT_VARIANT_ID,
    giftsFor: giftsFor,
    isB2G2Line: isB2G2Line,
    lastEligible: null   // set on first reconcile; null means "not yet known"
  };

  window.BunchCartUI = {
    applySections: applySections,
    refreshFromServer: refreshFromServer
  };

  /* ---------- LISTEN FOR ANY CART MUTATION ---------- */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?/;

  window.fetch = function () {
    var url = arguments[0];
    var href = typeof url === 'string' ? url : (url && url.url) || '';
    var isWrite = CART_WRITE.test(href) && !isInternal();
    var p = nativeFetch.apply(window, arguments);
    if (isWrite) p.then(function () { setTimeout(reconcile, 60); }).catch(function () {});
    return p;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__bunchCartWrite = CART_WRITE.test(url || '');
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__bunchCartWrite && !isInternal()) {
      this.addEventListener('load', function () { setTimeout(reconcile, 60); });
    }
    return nativeSend.apply(this, arguments);
  };

  /* ---------- RUN ON EVERY PAGE LOAD ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) reconcile(); });
})();
